import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { apiClient } from '../../apiClient';
import { useUser } from '../../hooks/UserContext';

const CompanyList = () => {
    const { systemAdminRole } = useUser();
    const isAdminRole = systemAdminRole === 'admin';

    const initialFilters = {
        company_name: '',
        billwave_customer_id: '',
        plan: '',
        status: ''
    };

    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [lastDocId, setLastDocId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState(initialFilters);
    const [error, setError] = useState(null);

    const fetchTenants = useCallback(async (isNext = false) => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });
            queryParams.append('limit', 50);
            if (isNext && lastDocId) {
                queryParams.append('lastDocId', lastDocId);
            }

            const response = await apiClient.get(`/v1/admin/tenants?${queryParams.toString()}`);
            const data = await response.json();

            if (response.ok) {
                if (isNext) {
                    setTenants(prev => [...prev, ...data]);
                } else {
                    setTenants(data);
                }
                if (data.length < 50) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                    setLastDocId(data[data.length - 1].tenant_id);
                }
            } else {
                setError(data.error || "データの取得に失敗しました。");
                console.error("API Error:", data);
            }
        } catch (error) {
            console.error("Error fetching tenants:", error);
            setError("通信エラーが発生しました。");
        } finally {
            setLoading(false);
        }
    }, [filters, lastDocId]);

    useEffect(() => {
        fetchTenants();
    }, []);

    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setLastDocId(null);
        setHasMore(true);
        fetchTenants();
    };

    const handleReset = () => {
        setFilters(initialFilters);
        setLastDocId(null);
        setHasMore(true);
        setTimeout(() => {
            fetchTenants();
        }, 0);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleCsvExport = async () => {
        if (!isAdminRole) return;
        setExporting(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await apiClient.get(`/v1/admin/tenants/export/csv?${queryParams.toString()}`);

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'CSVのエクスポートに失敗しました。');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `company_list_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Export error:', err);
            setError(err.message || 'CSVのエクスポート中にエラーが発生しました。');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">企業管理</h2>
                {isAdminRole && (
                    <button
                        type="button"
                        onClick={handleCsvExport}
                        disabled={exporting}
                        className="flex items-center bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 font-bold text-sm shadow-sm disabled:bg-green-300"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        {exporting ? 'CSV出力中...' : 'CSVダウンロード'}
                    </button>
                )}
            </div>

            {/* エラー表示エリア */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
                    <p className="text-sm text-red-700 font-bold">{error}</p>
                </div>
            )}

            {/* 検索フォーム */}
            <form onSubmit={handleSearch} className="bg-white p-6 rounded-lg shadow-md mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">会社名</label>
                        <input
                            type="text"
                            name="company_name"
                            value={filters.company_name}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">BillWaveお客様ID</label>
                        <input
                            type="text"
                            name="billwave_customer_id"
                            value={filters.billwave_customer_id}
                            onChange={handleFilterChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">プラン</label>
                            <select
                                name="plan"
                                value={filters.plan}
                                onChange={handleFilterChange}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="">すべて</option>
                                <option value="free">フリー</option>
                                <option value="lite">ライト</option>
                                <option value="standard">スタンダード</option>
                                <option value="business">ビジネス</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">ステータス</label>
                            <select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            >
                                <option value="">すべて</option>
                                <option value="active">有効</option>
                                <option value="suspended">停止中</option>
                                <option value="canceling">解約処理中</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="submit"
                        className="bg-indigo-600 text-white px-8 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-bold shadow-sm"
                    >
                        検索
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-bold"
                    >
                        リセット
                    </button>
                </div>
            </form>

            {/* 一覧テーブル */}
            <div className="bg-white shadow overflow-x-auto sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">会社名</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">BillWave顧客ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">プラン</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">利用開始日</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {tenants.map(tenant => (
                            <tr key={tenant.tenant_id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tenant.company_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.billwave_customer_id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tenant.plan}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {tenant.started_at ? new Date(tenant.started_at).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        tenant.status === 'active' ? 'bg-green-100 text-green-800' : 
                                        tenant.status === 'suspended' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {tenant.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link to={`/admin/companies/${tenant.tenant_id}`} className="text-indigo-600 hover:text-indigo-900">
                                        詳細
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hasMore && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={() => fetchTenants(true)}
                        disabled={loading}
                        className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {loading ? '読み込み中...' : '次の50件を読み込む'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CompanyList;
